import { george, i18n, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { themeColors } from "muiTheme";
import { useEffect } from "react";
import authStore from "stores/AuthStore";
import externalInterfaceStore, { IDeeplinkState } from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { getDefaultKeyPressHandler, safeWindowOpen } from "util/helpers";
import { performStoreHandoverTokenExchange } from "util/storeHandover";
import { ActivateJuniorCard } from "./components/cards/ActivateJuniorCard";
import { ChildContent } from "./components/cards/JuniorChild";
import { NoContentCard } from "./components/cards/NoContentCard";
import { JuniorInfoBox } from "./components/JuniorInfoBox";
import { ProductsSection } from "./components/ProductsSection";
import { JuniorRoutes } from "./JuniorRoutes";
import { JuniorStore } from "./JuniorStore";

type IProps = RouteComponentProps<{}> & i18n.InjectedIntlProps & {
    store: JuniorStore
};

const getHandoverUrl = (store: JuniorStore, productId: string) => {
    if (!store.selectedChild) {
        return;
    }

    const handoverUrl = store.getHandoverUrl(productId);
    if (!handoverUrl) {
        return;
    }

    const stateObject: IDeeplinkState = {
        srcClientId: externalInterfaceStore.clientId,
        returnState: "/overview",
        redirectUri: externalInterfaceStore.georgeBaseUrl + "/?at=c"
    };

    // Base64 encode the state object -> same as in AssetCalculation / BSPOptionsOverview
    return `${handoverUrl}${handoverUrl.includes("?") ? "&" : "?"}state=${btoa(JSON.stringify(stateObject))}`;
};

export const JuniorDetail = observer(({ store }: IProps) => {
    const ref = React.useRef<HTMLAnchorElement | null>(null);
    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;
    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;


    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    useEffect(() => {
        if (!store.selectedChild) {
            pushRoute(JuniorRoutes.OVERVIEW);
            return;
        }

        // Ensure products are loaded for the selected child
        if (store.productsLoadedForChildId !== store.selectedChildId && !store.isLoading) {
            store.loadProductsForSelectedChild();
        }
    }, [store.selectedChild, store.productsLoadedForChildId, store.selectedChildId, store.isLoading]);

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const handleActivation = async () => {
        pushRoute(JuniorRoutes.ACTIVATE_TOUR);
    };

    const handleBack = () => {
        pushRoute(JuniorRoutes.OVERVIEW);
    }

    const handleExistingProductNavigation = (productId: string, _channelProduct?: string, accountType?: string) => {
        const routePath: "saving" | "currentAccount" =
            accountType === "SAVING" ? "saving" : "currentAccount";

        const deeplink = `georgeapp://at/deeplink?path=${routePath}/${productId}`;
        const url = `${externalInterfaceStore.georgeBaseUrl}/index.html?crossPlatform=true&at=c#${routePath}/${productId}`;
        safeWindowOpen(
            externalInterfaceStore.isGeorgeApp ?
                deeplink :
                url,
            "_blank"
        );
    }

    const handleProductURLHandover = (productId: string) => {
        // Find the product to get its channel product for tracking
        const product = store.products.find(p => p.productId === productId);
        const productName = product?.channelProduct === "SMARTS" ? "SMARTSPAREN" : "SPARK7";
        trackingStore.trackEvent({ eventName: "gJuniorProductSelection", productKey: productName });

        const url = getHandoverUrl(store, productId);
        if (!url) {
            pushRoute("/error");
            return;
        }

        // Info 05.02.2026: When is George App (channel 10 / INT824BB), skip token exchange
        // and use existing token from authStore
        if (externalInterfaceStore.isGeorgeApp) {
            if (!authStore.accessToken) {
                console.error("### No access token available in authStore for GeorgeApp");
                pushRoute("/error");
                return;
            }

            const handoverUrl = `${url}#access_token=${authStore.accessToken}`;
            safeWindowOpen(handoverUrl);
        } else {
            // Perform token exchange for other channels
            performStoreHandoverTokenExchange(url);
        }
    }

    const handleProductDetailsNavigation = (productId: string) => {
        const url = store.getProductDetailsUrl(productId);
        if (!url) {
            return;
        }
        safeWindowOpen(url, "_blank");
    }

    const handleFeeInformationNavigation = (productId: string) => {
        const url = store.getFeeInformationUrl(productId);
        if (!url) {
            return;
        }
        safeWindowOpen(url);
    }

    const handleOpenProfileSettings = () => {
        const deeplink = "georgeapp://at/deeplink?path=junior-settings";
        const url = `${externalInterfaceStore.georgeBaseUrl}/index.html?crossPlatform=true&at=c#settings`;
        safeWindowOpen(
            externalInterfaceStore.isGeorgeApp ? deeplink : url,
            "_blank"
        );
    };

    const isOldEnough = store.selectedChild?.juniorValue !== "tooYoung" && store.selectedChild?.juniorValue !== undefined;
    const canActivate = store.selectedChild?.juniorValue !== "active";
    const juniorValue = store.selectedChild?.juniorValue;
    const showGeorgeJuniorActivationDisclaimer =
        juniorValue === "activationNeeded" || juniorValue === "migrationNeeded";

    const hasExistingProducts = store.existingProducts.length > 0;
    const hasAvailableProducts = store.availableProducts.length > 0;

    return (

        <BackgroundComponent style={{ paddingTop: 24, paddingBottom: theme.MARGIN }}>
            <george.PageWithActionButton>
                <pr.A11yPopupHider>
                    <div style={pr.STYLE_CENTERED_CARD}>
                        <a
                            ref={ref}
                            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexGrow: 1, flexBasis: 0 }}
                            role="button"
                            tabIndex={0}
                            onClick={handleBack}
                            onKeyPress={getDefaultKeyPressHandler(handleBack)}
                            aria-label={ui.__("a11y.back")}
                        >
                            <pr.IconChevronLeft className="theme-secondary-action-label-color" style={{ display: "block", flexShrink: 0 }} />
                            {!isMobile && (
                                <pr.P_16_CONTENT className="theme-secondary-action-label-color" style={{ fontWeight: 600 }}>
                                    {ui.__("a11y.back")}
                                </pr.P_16_CONTENT>
                            )}
                        </a>
                    </div>
                </pr.A11yPopupHider>

                <george.Card
                    elevated={false}
                    style={{ borderRadius: 8, padding: theme.MARGIN, }}
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <ChildContent
                            name={store.selectedChild?.name ?? ""}
                            juniorValue={store.selectedChild?.juniorValue}
                            gender={store.selectedChild?.gender ?? "UNKNOWN"}
                        />
                    </div>
                </george.Card>

                {juniorValue === "active" && (
                    <div style={{ ...pr.STYLE_CENTERED_CARD, padding: `0 ${theme.MARGIN}`, marginTop: theme.MARGIN_SMALL }}>
                        <JuniorInfoBox>
                            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <pr.P_14_CONTENT style={{ margin: 0, lineHeight: "20px", color: ui.isDarkMode ? themeColors.grey_100 : themeColors.george_text_color }}>
                                    {ui.__("gJunior.detail.profileInfoBox.text")}
                                </pr.P_14_CONTENT>
                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: theme.MARGIN_SMALL }}>
                                    <a
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleOpenProfileSettings}
                                        onKeyPress={getDefaultKeyPressHandler(handleOpenProfileSettings)}
                                        style={{
                                            color: themeColors.georgeBlue,
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            fontSize: 14,
                                            textDecoration: "none",
                                        }}
                                    >
                                        {ui.__("gJunior.activation.download.continue")}
                                    </a>
                                </div>
                            </div>
                        </JuniorInfoBox>
                    </div>
                )}

                {canActivate && (
                    <>
                        <div
                            style={{
                                ...pr.STYLE_CENTERED_CARD,
                                padding: `0 ${theme.MARGIN}`,
                            }}
                        >
                            <pr.P_18_TITLE
                                style={{
                                    fontWeight: 600,
                                    marginTop: theme.MARGIN_LARGE,
                                    marginBottom: theme.MARGIN_SMALL
                                }}
                            >
                                {ui.__("gJunior.activate.title")}
                            </pr.P_18_TITLE>
                            {showGeorgeJuniorActivationDisclaimer && (
                                <>
                                    <pr.P_16_CONTENT
                                        className="theme-title-color"
                                        style={{
                                            margin: 0,
                                            marginBottom: theme.MARGIN_SMALL,
                                            lineHeight: "22px",
                                        }}
                                    >
                                        <strong>{ui.__("gJunior.activate.disclaimer.accountsLead")}</strong>
                                        {" "}
                                        {ui.__("gJunior.activate.disclaimer.accountsBody")}
                                    </pr.P_16_CONTENT>
                                    {juniorValue === "migrationNeeded" && (
                                        <pr.P_16_CONTENT
                                            className="theme-title-color"
                                            style={{
                                                margin: 0,
                                                marginBottom: theme.MARGIN_SMALL,
                                                lineHeight: "22px",

                                            }}
                                        >
                                            {ui.__("gJunior.activate.migration.info")}
                                        </pr.P_16_CONTENT>
                                    )}
                                </>
                            )}
                        </div>
                        <ActivateJuniorCard isOldEnough={isOldEnough} handleActivation={handleActivation} />
                    </>
                )}

                {!hasExistingProducts && !hasAvailableProducts ? (
                    <div style={{ padding: `0 ${theme.MARGIN}` }}>
                        <NoContentCard title={ui.__("gJunior.noProducts.title")} description={ui.__("gJunior.noProducts.description")} imgSrc={require("../../assets/no_product_available.svg")} />
                    </div>
                ) : (
                        <>
                            {hasAvailableProducts && (
                                <>
                                    <ProductsSection
                                        title={ui.__("gJunior.availableProducts.title")}
                                        products={store.availableProducts}
                                        store={store}
                                        isNewProduct={true}
                                        onOpenAccountButtonCLick={handleProductURLHandover}
                                        onProductDetailsClick={handleProductDetailsNavigation}
                                        onFeeInformationClick={handleFeeInformationNavigation}
                                    />
                                </>
                            )}
                            {hasExistingProducts && (
                                <ProductsSection
                                    title={ui.__("gJunior.existingProducts.title")}
                                    products={store.existingProducts}
                                    onProductClick={handleExistingProductNavigation}
                                    store={store}
                                />
                            )}
                        </>
                    )}
            </george.PageWithActionButton>
        </BackgroundComponent >
    );
});



// WEBPACK FOOTER //
// ./src/components/gJunior/JuniorDetail.tsx